import { supabaseClient } from './supabase';
import { toast } from './toast';

interface DataForSEOCredentials {
  login: string;
  password: string;
  base_url: string;
}

interface DataForSEOResult {
  url: string;
  title: string;
  description: string;
  position: number;
  domain: string;
  breadcrumb: string;
  links_count: number;
  main_domain: string;
  relative_url: string;
  etv: number;
  estimated_paid_traffic_cost: number;
  rank_group: number;
  xpath: string;
}

async function getCredentials(): Promise<DataForSEOCredentials> {
  const { data, error } = await supabaseClient
    .from('api_services')
    .select('api_key, config, is_active')
    .eq('service_type', 'dataforseo')
    .single();

  if (error) {
    toast.error('Failed to fetch DataForSEO credentials');
    throw new Error('Failed to fetch DataForSEO credentials');
  }
  if (!data) {
    toast.error('DataForSEO service not found');
    throw new Error('DataForSEO service not found');
  }
  if (!data.is_active) {
    toast.error('DataForSEO service is not active');
    throw new Error('DataForSEO service is not active');
  }

  return {
    login: data.config.login,
    password: data.api_key,
    base_url: data.config.base_url || 'https://api.dataforseo.com/v3/serp/google/organic/live/advanced'
  };
}

export async function createSerpTask(keyword: string, locationCode: string, language: string = 'uk'): Promise<DataForSEOResult[]> {
  try {
    console.log('Sending request to SERP Edge Function:', {
      keyword,
      locationCode,
      language
    });

    // Get current user session for authorization
    const { data: { session } } = await supabaseClient.auth.getSession();
    if (!session) {
      throw new Error('User not authenticated');
    }

    // Call our Edge Function
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const response = await fetch(`${supabaseUrl}/functions/v1/dataforseo-proxy`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        keyword,
        locationCode,
        language
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Edge Function error:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      toast.error(`SERP API error: ${response.status} ${response.statusText}`);
      throw new Error(`SERP API responded with status: ${response.status}`);
    }

    const result = await response.json();
    console.log('SERP Edge Function response:', result);
    
    if (!result.success) {
      throw new Error(result.error || 'Unknown error from SERP API');
    }

    toast.success(`Loaded ${result.data.length} SERP results`);
    return result.data || [];

  } catch (error) {
    console.error('SERP proxy error:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to fetch SERP results');
    throw new Error(error instanceof Error ? error.message : 'Failed to fetch SERP results');
  }
}

export async function analyzeSerpResults(results: DataForSEOResult[]): Promise<number> {
  if (!results.length) {
    console.warn('No results to analyze');
    return 0;
  }

  console.log('Analyzing results:', results.length);

  // Analyze TOP-10 results to calculate the difficulty score
  const difficultyFactors = {
    domainAuthority: 0.4,
    contentQuality: 0.3,
    competitionLevel: 0.3
  };

  const domainAuthorityScore = results.reduce((score, result, index) => {
    const position = 10 - index;
    const linkScore = Math.min((result.links_count || 0) / 1000, 1);
    return score + (linkScore * position) / 55;
  }, 0);

  const contentQualityScore = results.reduce((score, result) => {
    const descriptionLength = result.description?.length || 0;
    const titleLength = result.title?.length || 0;
    const isOptimalLength = descriptionLength > 120 && descriptionLength < 160 && 
                           titleLength > 50 && titleLength < 60;
    return score + (isOptimalLength ? 1 : 0.5);
  }, 0) / results.length;

  const competitionScore = results.reduce((score, result) => {
    const hasPaidTraffic = (result.estimated_paid_traffic_cost || 0) > 0;
    const isStrongDomain = result.etv > 1000;
    return score + (hasPaidTraffic ? 0.5 : 0) + (isStrongDomain ? 0.5 : 0);
  }, 0) / results.length;

  const difficultyScore = Math.round(
    (domainAuthorityScore * difficultyFactors.domainAuthority +
     contentQualityScore * difficultyFactors.contentQuality +
     competitionScore * difficultyFactors.competitionLevel) * 100
  );

  console.log('Analysis scores:', {
    domainAuthorityScore,
    contentQualityScore,
    competitionScore,
    finalScore: difficultyScore
  });

  return Math.min(Math.max(difficultyScore, 0), 100);
}